// Analytics Service
// Handles all analytics-related API calls for admin reporting
import { v1Api } from './api.js';
import { getV1EndpointUrl } from '../config/api.js';

class AnalyticsService {

  // ============================================================================
  // BOOKING ANALYTICS
  // ============================================================================

  /**
   * Get booking analytics with filters
   * @param {Object} filters - Date range, status, type filters
   * @returns {Promise} Analytics data
   */
  async getBookingAnalytics(filters = {}) {
    try {
      console.log('📊 [Analytics] Fetching booking analytics with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/bookings');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('📊 [Analytics] Booking analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching booking analytics:', error);
      throw error;
    }
  }

  /**
   * Get booking logs with detailed information
   * @param {Object} filters - Pagination, date range, search filters
   * @returns {Promise} Booking logs data
   */
  async getBookingLogs(filters = {}) {
    try {
      console.log('📋 [Analytics] Fetching booking logs with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/booking-logs');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('📋 [Analytics] Booking logs response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching booking logs:', error);
      throw error;
    }
  }

  /**
   * Get booking status analytics
   * @param {Object} filters - Date range filters
   * @returns {Promise} Status analytics data
   */
  async getBookingStatusAnalytics(filters = {}) {
    try {
      console.log('📈 [Analytics] Fetching booking status analytics with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/booking-status');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('📈 [Analytics] Booking status analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching booking status analytics:', error);
      throw error;
    }
  }

  // ============================================================================
  // PAYMENT ANALYTICS
  // ============================================================================

  /**
   * Get payment analytics
   * @param {Object} filters - Date range, status filters
   * @returns {Promise} Payment analytics data
   */
  async getPaymentAnalytics(filters = {}) {
    try {
      console.log('💰 [Analytics] Fetching payment analytics with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/payments');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('💰 [Analytics] Payment analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching payment analytics:', error);
      throw error;
    }
  }

  /**
   * Get payment logs with transaction details
   * @param {Object} filters - Pagination, date range, search filters
   * @returns {Promise} Payment logs data
   */
  async getPaymentLogs(filters = {}) {
    try {
      console.log('💳 [Analytics] Fetching payment logs with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/payment-logs');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('💳 [Analytics] Payment logs response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching payment logs:', error);
      throw error;
    }
  }

  /**
   * Get revenue analytics
   * @param {Object} filters - Date range, granularity (daily, weekly, monthly)
   * @returns {Promise} Revenue analytics data
   */
  async getRevenueAnalytics(filters = {}) {
    try {
      console.log('📊 [Analytics] Fetching revenue analytics with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/revenue');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('📊 [Analytics] Revenue analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching revenue analytics:', error);
      throw error;
    }
  }

  // ============================================================================
  // DASHBOARD OVERVIEW
  // ============================================================================

  /**
   * Get dashboard overview statistics
   * @param {Object} filters - Date range filters
   * @returns {Promise} Dashboard overview data
   */
  async getDashboardOverview(filters = {}) {
    try {
      console.log('📊 [Analytics] Fetching dashboard overview with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/dashboard-overview');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('📊 [Analytics] Dashboard overview response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching dashboard overview:', error);
      throw error;
    }
  }

  /**
   * Get real-time metrics
   * @returns {Promise} Real-time metrics data
   */
  async getRealTimeMetrics() {
    try {
      console.log('⚡ [Analytics] Fetching real-time metrics');

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/real-time-metrics');
      const response = await v1Api.get(endpoint);

      console.log('⚡ [Analytics] Real-time metrics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching real-time metrics:', error);
      throw error;
    }
  }

  // ============================================================================
  // PERFORMANCE ANALYTICS
  // ============================================================================

  /**
   * Get ambulance performance analytics
   * @param {Object} filters - Date range, ambulance type filters
   * @returns {Promise} Ambulance performance data
   */
  async getAmbulancePerformance(filters = {}) {
    try {
      console.log('🚑 [Analytics] Fetching ambulance performance with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/ambulance-performance');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('🚑 [Analytics] Ambulance performance response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching ambulance performance:', error);
      throw error;
    }
  }

  /**
   * Get driver performance analytics
   * @param {Object} filters - Date range, driver filters
   * @returns {Promise} Driver performance data
   */
  async getDriverPerformance(filters = {}) {
    try {
      console.log('👨‍⚕️ [Analytics] Fetching driver performance with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/driver-performance');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('👨‍⚕️ [Analytics] Driver performance response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching driver performance:', error);
      throw error;
    }
  }

  /**
   * Get hospital partnership analytics
   * @param {Object} filters - Date range, hospital filters
   * @returns {Promise} Hospital performance data
   */
  async getHospitalPerformance(filters = {}) {
    try {
      console.log('🏥 [Analytics] Fetching hospital performance with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/hospital-performance');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('🏥 [Analytics] Hospital performance response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching hospital performance:', error);
      throw error;
    }
  }

  // ============================================================================
  // GEOGRAPHIC ANALYTICS
  // ============================================================================

  /**
   * Get geographic analytics (hotspots, coverage areas)
   * @param {Object} filters - Date range, location filters
   * @returns {Promise} Geographic analytics data
   */
  async getGeographicAnalytics(filters = {}) {
    try {
      console.log('🗺️ [Analytics] Fetching geographic analytics with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/geographic');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('🗺️ [Analytics] Geographic analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching geographic analytics:', error);
      throw error;
    }
  }

  // ============================================================================
  // EXPORT FUNCTIONALITY
  // ============================================================================

  /**
   * Export analytics data to various formats
   * @param {Object} exportOptions - Type, filters, format options
   * @returns {Promise} Export response
   */
  async exportAnalyticsData(exportOptions) {
    try {
      console.log('📤 [Analytics] Exporting analytics data with options:', exportOptions);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/export');
      const response = await v1Api.post(endpoint, exportOptions);

      console.log('📤 [Analytics] Export response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error exporting analytics data:', error);
      throw error;
    }
  }

  /**
   * Get export history
   * @param {Object} filters - Pagination filters
   * @returns {Promise} Export history data
   */
  async getExportHistory(filters = {}) {
    try {
      console.log('📁 [Analytics] Fetching export history with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/export-history');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('📁 [Analytics] Export history response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching export history:', error);
      throw error;
    }
  }

  /**
   * Download exported file
   * @param {string} exportId - Export ID
   * @returns {Promise} File download response
   */
  async downloadExport(exportId) {
    try {
      console.log('⬇️ [Analytics] Downloading export file:', exportId);

      const endpoint = getV1EndpointUrl(`/v1/admin/analytics/export/${exportId}/download`);
      const response = await v1Api.get(endpoint, { responseType: 'blob' });

      console.log('⬇️ [Analytics] Download response received');
      return response;
    } catch (error) {
      console.error('❌ [Analytics] Error downloading export file:', error);
      throw error;
    }
  }

  // ============================================================================
  // FILTERED DATA QUERIES
  // ============================================================================

  /**
   * Get filtered bookings with advanced search
   * @param {Object} searchFilters - Advanced search and filter options
   * @returns {Promise} Filtered booking data
   */
  async getFilteredBookings(searchFilters) {
    try {
      console.log('🔍 [Analytics] Searching bookings with filters:', searchFilters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/bookings/search');
      const response = await v1Api.post(endpoint, searchFilters);

      console.log('🔍 [Analytics] Filtered bookings response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error searching bookings:', error);
      throw error;
    }
  }

  /**
   * Get filtered payments with advanced search
   * @param {Object} searchFilters - Advanced search and filter options
   * @returns {Promise} Filtered payment data
   */
  async getFilteredPayments(searchFilters) {
    try {
      console.log('🔍 [Analytics] Searching payments with filters:', searchFilters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/payments/search');
      const response = await v1Api.post(endpoint, searchFilters);

      console.log('🔍 [Analytics] Filtered payments response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error searching payments:', error);
      throw error;
    }
  }

  // ============================================================================
  // TREND ANALYSIS
  // ============================================================================

  /**
   * Get booking trends over time
   * @param {Object} filters - Date range, granularity
   * @returns {Promise} Booking trends data
   */
  async getBookingTrends(filters = {}) {
    try {
      console.log('📈 [Analytics] Fetching booking trends with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/trends/bookings');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('📈 [Analytics] Booking trends response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching booking trends:', error);
      throw error;
    }
  }

  /**
   * Get payment trends over time
   * @param {Object} filters - Date range, granularity
   * @returns {Promise} Payment trends data
   */
  async getPaymentTrends(filters = {}) {
    try {
      console.log('📈 [Analytics] Fetching payment trends with filters:', filters);

      const endpoint = getV1EndpointUrl('/v1/admin/analytics/trends/payments');
      const response = await v1Api.get(endpoint, { params: filters });

      console.log('📈 [Analytics] Payment trends response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Analytics] Error fetching payment trends:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;